import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Alert } from 'antd';
import {
  LOGIN_ROUTE,
  SUBSCRIPTION_SIGN_UP_ROUTE,
} from '../../app/appConstants';
import { SubscriptionLoading } from '../../features/registration';
import {
  NewPostTrialDiscountSubscriptionCheckoutRequest,
  useCreateNewPostTrialDiscountCheckoutMutation,
} from '../../features/registration/registrationSlice';
import { useGetUserIdQuery } from '../../features/users/usersSlice';
import { STRIPE_INTERVALS, StripeInterval } from '../../utils/payment-utils';

export default function PostTrialDiscountPreCheckoutPage(): React.JSX.Element {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const interval = (searchParams.get('interval') ?? 'month') as StripeInterval;
  const userIdParam = searchParams.get('userId');
  const { data: userIds, isFetching: loadingUserId } = useGetUserIdQuery(
    undefined,
    { skip: Boolean(userIdParam) }
  );
  const userId = userIdParam || userIds?.user_id;
  const [error, setError] = useState<string | null>(null);

  const [createDiscountSession] =
    useCreateNewPostTrialDiscountCheckoutMutation();

  useEffect(() => {
    if (!userId && !loadingUserId) {
      // Redirect to log-in form
      const params = new URLSearchParams();
      params.set('redirect', location.pathname + location.search);
      window.location.href = `${LOGIN_ROUTE}?${params.toString()}`;
      return;
    }
    if (!interval || !STRIPE_INTERVALS.includes(interval)) {
      setError(`Invalid interval: "${interval ?? 'null'}"`);
      return;
    }

    (async () => {
      // We use .../success here rather than .../pre-success because we already know the user is not a guest, and we know their userId
      const successUrl =
        `${window.location.protocol}//${window.location.host}` +
        `${SUBSCRIPTION_SIGN_UP_ROUTE}/success?plan=Pro&userId=${userId}&isTrialUpgrade=true`;

      if (!userId) {
        return;
      }

      const sessionParams: NewPostTrialDiscountSubscriptionCheckoutRequest = {
        user_id: userId,
        interval: interval,
        success_url: successUrl,
        refresh_url: window.location.href,
        cancel_url: window.location.href,
      };
      createDiscountSession(sessionParams)
        .unwrap()
        .then((redirect_url) => {
          if (redirect_url) {
            window.location.href = redirect_url;
          }
        })
        .catch((err) => {
          setError(
            'Error requesting discount: ' + err?.data?.detail ?? err?.data
          );
        });
    })();
  }, [
    createDiscountSession,
    interval,
    userId,
    location,
    userIdParam,
    loadingUserId,
    searchParams,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>swimbooker+ | Subscribe Today!</title>
      </Helmet>
      <div className='grid h-full min-h-screen w-full place-content-center bg-gradient-to-b from-blue to-[#1B4C74] px-6 py-4 text-white min-[400px]:px-6'>
        <div className='mx-auto my-auto max-w-sm text-center'>
          {!userId ? (
            <SubscriptionLoading hideMessage />
          ) : error ? (
            <Alert
              message='Something went wrong!'
              description={
                <p>
                  {error ??
                    'Please try reloading the page or contact our support team!'}
                </p>
              }
              type='error'
              showIcon
            />
          ) : (
            <SubscriptionLoading hideMessage />
          )}
        </div>
      </div>
    </>
  );
}
